import React, { useMemo, useState } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
library.add(fas)

import { getRequest } from "@react/helpers/fetch"
import DashboardTabs from "../chantiers_dashboard/components/DashboardTabs"
import { MultiSelect } from "../chantiers_dashboard/components/FilterBar"
import Tiles from "./components/Tiles"
import PortfolioTable from "./components/PortfolioTable"
import { t, FLAG_TONES } from "./format"

const FLAGS = ["risk", "watch", "ok", "idle"]

const queryOf = filters => {
  const params = new URLSearchParams()
  ;(filters.statuses    || []).forEach(value => params.append("statuses[]", value))
  ;(filters.manager_ids || []).forEach(value => params.append("manager_ids[]", value))
  return params.toString()
}

const App = ({ data: initialData, json_url, export_url }) => {
  const [data, setData]       = useState(initialData || {})
  const [filters, setFilters] = useState(initialData?.filters || {})
  const [loading, setLoading] = useState(false)
  const [flag, setFlag]       = useState("")
  const [query, setQuery]     = useState("")

  const refresh = next => {
    setFilters(next)
    setLoading(true)
    const qs = queryOf(next)
    getRequest(`${json_url}${qs ? `?${qs}` : ""}`)
      .then(response => { if (response && !response.errors) setData(response) })
      .finally(() => setLoading(false))
  }

  const projects = data.projects || []
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return projects.filter(row =>
      (!flag || row.flag === flag) &&
      (!needle || `${row.name} ${row.client || ""} ${row.manager || ""}`.toLowerCase().includes(needle)))
  }, [projects, flag, query])

  const counts = data.totals?.flags || {}
  const qs = queryOf(filters)

  return (
    <div className="pb-12">
      <div className="px-7 pt-6 pb-2 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900">{t("title")}</h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t("subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <DashboardTabs active="hours" />
          <a href={`${export_url}${qs ? `?${qs}` : ""}`}
             className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-[13px] font-medium text-slate-700 hover:bg-slate-50">
            <FontAwesomeIcon icon="file-excel" className="w-3.5 h-3.5 text-emerald-600" />{t("export.button")}
          </a>
        </div>
      </div>

      <div className="px-7 mt-3 flex items-center gap-2 flex-wrap">
        <MultiSelect
          label    = {t("filters.status")}
          icon     = "circle-half-stroke"
          options  = {data.filter_options?.statuses || []}
          selected = {filters.statuses || []}
          onChange = {statuses => refresh({ ...filters, statuses })}
        />
        <MultiSelect
          label    = {t("filters.manager")}
          icon     = "user-tie"
          options  = {data.filter_options?.managers || []}
          selected = {filters.manager_ids || []}
          onChange = {manager_ids => refresh({ ...filters, manager_ids })}
        />
        <div className="relative">
          <FontAwesomeIcon icon="magnifying-glass" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
          <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={t("filters.search")}
                 className="h-8 w-56 pl-8 pr-2 rounded-md border border-slate-200 bg-white text-[12.5px] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-200" />
        </div>
        {loading && <FontAwesomeIcon icon="circle-notch" spin className="text-slate-400 ml-1" />}
      </div>

      <div className="px-7 mt-4">
        <Tiles totals={data.totals} currency={data.currency} />
      </div>

      <div className="px-7 mt-6 mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 rounded-md bg-slate-100 p-1 text-[12.5px] font-medium">
          <button type="button" onClick={() => setFlag("")}
                  className={`h-7 px-3 rounded transition-colors ${!flag ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {t("flags.all")} <span className="text-slate-400 font-normal tabular-nums">{projects.length}</span>
          </button>
          {FLAGS.map(key => (
            <button key={key} type="button" onClick={() => setFlag(current => (current === key ? "" : key))}
                    className={`h-7 px-3 inline-flex items-center gap-1.5 rounded transition-colors ${flag === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <span className={`h-2 w-2 rounded-full ${FLAG_TONES[key].dot}`} />
              {t(`flags.${key}`)} <span className="text-slate-400 font-normal tabular-nums">{counts[key] || 0}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-[11.5px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><i className="inline-block h-1.5 w-[20px] rounded-full bg-emerald-500" />{t("legend.consumed")}</span>
          <span className="inline-flex items-center gap-1.5"><i className="inline-block h-3 w-[2px] rounded bg-slate-800" />{t("legend.progress")}</span>
          <span className="inline-flex items-center gap-1.5"><FontAwesomeIcon icon="circle-info" className="text-slate-400" />{t("legend.performance")}</span>
        </div>
      </div>

      <div className="px-7">
        <PortfolioTable projects={visible} totals={data.totals} currency={data.currency} />
      </div>
    </div>
  )
}

export default App
